import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { EventTicketPackagesFacade } from '@galabid/gb-dashboard/event-tickets/data-access-event-ticket-packages'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import * as EventTicketPackagesActions from 'libs/gb-dashboard/event-tickets/data-access-event-ticket-packages/src/lib/+state/event-ticket-packages.actions'
import { EventTicketsConstants } from 'libs/gb-dashboard/event-tickets/feature/src/lib/event-tickets-constants'
import { catchError, exhaustMap, filter, finalize, map, mergeMap, of, switchMap, tap, withLatestFrom, zip } from 'rxjs'
import { EventTicketPurchasesApiService } from './event-ticket-purchases-api.service'
import * as EventTicketPurchasesActions from './event-ticket-purchases.actions'
import { EventTicketPurchasesFacade } from './event-ticket-purchases.facade'
import {
  EVENT_TICKET_PURCHASES_LOADER,
  EventTicketPurchasePayload,
  EventTicketPurchasesEntity,
} from './event-ticket-purchases.models'
import { GbDashboardDialogService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-dialog-service/gb-dashboard-dialog.service'
import { TranslateService } from '@ngx-translate/core'
import { GbEventTicketPurchaseStepperViewController } from 'libs/gb-dashboard/event-tickets/feature/src/lib/gb-event-ticket-purchase-dialog/gb-event-ticket-purchase-stepper-view-controller.service'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbStripeService } from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/gb-stripe.service'
import * as StripeActions from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/+state/stripe.actions'
import { PaymentProcessEnum } from 'libs/shared/models/payment-method/payment-method.models'

@Injectable()
export class EventTicketPurchasesEffects {
  loadEventTicketPurchases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.loadEventTicketPurchases),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.eventTicketPurchasesFacade.allEventTicketPurchases$),
      switchMap(([action, urlName, allEventTicketPurchases]) => {
        const offset = action.shouldReset ? 0 : allEventTicketPurchases.length || 0

        return this.eventTicketPurchasesApiService
          .getEventTicketPurchases(urlName || '', offset, action.searchTerm, action.order, action.refunded)
          .pipe(
            map((response: GbPaginatedResponse<EventTicketPurchasesEntity>) => {
              const payload = {
                count: response.count,
                eventTicketPurchases: response.results,
              }

              return action.shouldReset
                ? EventTicketPurchasesActions.setEventTicketPurchases(payload)
                : EventTicketPurchasesActions.loadEventTicketPurchasesSuccess(payload)
            }),
            catchError((error) => {
              return of(EventTicketPurchasesActions.loadEventTicketPurchasesFailure({ error }))
            }),
          )
      }),
    ),
  )

  syncEventTicketPurchaseForRealtime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.syncEventTicketPurchaseForRealtime),
      filter(() =>
        // only sync if admin is on event ticket purchases list
        this.router.url.includes(`${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.PURCHASES_LIST}`),
      ),
      withLatestFrom(
        this.eventTicketPurchasesFacade.allEventTicketPurchases$,
        this.eventTicketPurchasesFacade.searchTerm$,
        this.eventTicketPurchasesFacade.order$,
        this.eventTicketPurchasesFacade.refunded$,
      ),
      map(([action, allEventTicketPurchases, searchTerm, order, refunded]) => {
        const isPurchaseInList = allEventTicketPurchases.some((purchase) => purchase.uuid === action.uuid)

        return isPurchaseInList
          ? EventTicketPurchasesActions.syncEventTicketPurchase({ uuid: action.uuid })
          : EventTicketPurchasesActions.loadEventTicketPurchases({ searchTerm, order, refunded, shouldReset: true })
      }),
    ),
  )

  syncEventTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.syncEventTicketPurchase),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.eventTicketPurchasesApiService.syncEventTicketPurchase(urlName, action.uuid).pipe(
          tap((response) => action.onSuccess?.(response)),
          map((response) =>
            EventTicketPurchasesActions.syncEventTicketPurchaseSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
            }),
          ),
          catchError(async (error: HttpErrorResponse) =>
            EventTicketPurchasesActions.syncEventTicketPurchaseFailure({ error }),
          ),
        ),
      ),
    ),
  )

  placeEventTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.placeEventTicketPurchase),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.eventTicketPackagesFacade.selectedEventTicketPackage$),
      mergeMap(([action, urlName, selectedTicketPackage]) => {
        const payload = <EventTicketPurchasePayload>{
          ...action.payload,
          ticket_package: { uuid: selectedTicketPackage?.uuid },
        }

        return this.eventTicketPurchasesApiService.createEventTicketPurchase(urlName, payload).pipe(
          map((response) =>
            EventTicketPurchasesActions.placeEventTicketPurchaseSuccess({ eventTicketPurchase: response }),
          ),
          catchError(async (error: HttpErrorResponse) =>
            EventTicketPurchasesActions.placeEventTicketPurchaseFailure({ error }),
          ),
        )
      }),
    ),
  )

  progressEventTicketPurchaseStepper$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess),
        filter((action) => !action.eventTicketPurchase.request_invoice_pending),
        tap(() => this.gbEventTicketPurchaseStepperViewController.progressStepper()),
      ),
    { dispatch: false },
  )

  // Only stripe payments require stripe elements to be initialized
  placeEventTicketPurchaseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess),
      filter((action) => !action.eventTicketPurchase.request_invoice_pending),
      filter(
        (action) => action.eventTicketPurchase?.payment?.payment_method_payment_process === PaymentProcessEnum.Stripe,
      ),
      tap((action) => (this.gbStripeService.payment = action.eventTicketPurchase?.payment)),
      map(() => StripeActions.initStripeElements({})),
    ),
  )

  finaliseEventTicketPurchaseInvoiceRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess),
      filter((action) => !!action.eventTicketPurchase.request_invoice_pending),
      map(() => EventTicketPurchasesActions.finaliseEventTicketPurchaseSuccess()), // skip 'assign' tickets step if invoice requested
    ),
  )

  finaliseEventTicketPurchaseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.finaliseEventTicketPurchaseSuccess),
      tap(() => this.gbNotificationService.success('EVENT_TICKET_PURCHASES.PURCHASE_SUCCESS')),
      map(() => EventTicketPackagesActions.closeEventTicketPurchaseDialog()),
    ),
  )

  issueEventTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.issueEventTicketPurchase),
      tap(() => this.gbLoaderService.showLoaderDialog(EVENT_TICKET_PURCHASES_LOADER.issuePurchase.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      mergeMap(([action, urlName]) =>
        this.eventTicketPurchasesApiService.issueEventTicketPurchase(urlName, action.uuid).pipe(
          map(() => EventTicketPurchasesActions.issueEventTicketPurchaseSuccess()),
          catchError((error) => of(EventTicketPurchasesActions.issueEventTicketPurchaseFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(EVENT_TICKET_PURCHASES_LOADER.issuePurchase.id)),
        ),
      ),
    ),
  )

  issueEventTicketPurchaseSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.issueEventTicketPurchaseSuccess),
        tap(() => this.gbNotificationService.success('EVENT_TICKET_PURCHASES.MARK_AS_PAID.SUCCESS')),
      ),
    { dispatch: false },
  )

  refundTicketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.refundTicketSuccess),
      tap((action) =>
        this.gbNotificationService.success(
          action.deleteTicketsOnly
            ? 'EVENT_TICKET_PURCHASES.REFUND.DELETE_SUCCESS'
            : 'EVENT_TICKET_PURCHASES.REFUND.REFUND_SUCCESS',
        ),
      ),
      map(() => EventTicketPackagesActions.closeEventTicketPurchaseDialog()),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPurchasesActions.loadEventTicketPurchasesFailure,
          EventTicketPurchasesActions.placeEventTicketPurchaseFailure,
          EventTicketPurchasesActions.placeEventTicketPurchaseFailure,
          EventTicketPurchasesActions.refundTicketFailure,
          EventTicketPurchasesActions.issueEventTicketPurchaseFailure,
        ),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbNotificationService.error(error ?? 'GENERIC.ERROR')
        }),
      ),
    { dispatch: false },
  )

  refundTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.refundTicket),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(EVENT_TICKET_PURCHASES_LOADER.refundPurchase.id)
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      mergeMap(([action, urlName]) =>
        this.eventTicketPurchasesApiService.refundTicketPurchase(urlName as string, action.uuid, action.tickets).pipe(
          map(() => {
            action.onSuccess()
            return EventTicketPurchasesActions.refundTicketSuccess({ deleteTicketsOnly: action.deleteTicketsOnly })
          }),
          catchError((error) => of(EventTicketPurchasesActions.refundTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(EVENT_TICKET_PURCHASES_LOADER.refundPurchase.id)),
        ),
      ),
    ),
  )

  openEventTicketPurchaseMarkAsPaidDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.openEventTicketPurchaseMarkAsPaidDialog),
      exhaustMap((action) => {
        const dialogData = {
          title: 'EVENT_TICKET_PURCHASES.MARK_AS_PAID.TITLE',
          body: this.translateService.instant('EVENT_TICKET_PURCHASES.MARK_AS_PAID.DESCRIPTION', {
            participant: `${action.eventTicketPurchase.profile?.first_name} ${action.eventTicketPurchase.profile?.last_name}`,
          }),
          confirmButtonText: 'EVENT_TICKET_PURCHASES.MARK_AS_PAID.CONFIRM',
          cancelButtonText: 'CANCEL',
        }
        return zip([of(action), this.gbDashboardDialogService.openConfirmDialog(dialogData)])
      }),
      filter(([, result]) => !!result),
      map(([action]) =>
        EventTicketPurchasesActions.issueEventTicketPurchase({ uuid: action.eventTicketPurchase.uuid }),
      ),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private eventTicketPurchasesApiService: EventTicketPurchasesApiService,
    private eventTicketPackagesFacade: EventTicketPackagesFacade,
    private eventTicketPurchasesFacade: EventTicketPurchasesFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbEventTicketPurchaseStepperViewController: GbEventTicketPurchaseStepperViewController,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbLoaderService: GbLoaderService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private router: Router,
    private translateService: TranslateService,
    private gbStripeService: GbStripeService,
  ) {}
}
